/* eslint-env browser */
// Admin registry of partner NGOs for the Donkey Mile donation flow (wave 3,
// W2_Portal_Tables look). The mobile Donate screen lists active entries from
// /ngos; donation totals live separately in server-only /ngo_stats.
//
// Reads and writes are the old page's: fetchNgos() and fetchNgoStats() once
// on open; the logo: uploadNgoImage(id, file) then
// editNgo({ ...ngo, image: url }, 'Update') and fetchNgos(); Delete:
// editNgo(ngo, 'Delete') then fetchNgos() (critical edits on). The plain NGO
// record is passed (no material-table tableData).
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { api } from 'common';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { DataTable, PageHeader, formatMoney, formatNumber, usePageState } from '../components/modern';
import { formatAddisDateTime } from '../common/exportUtils';
import { tokens as c } from '../theme';

export default function Ngos() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const { editNgo, fetchNgos, uploadNgoImage } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const ngodata = useSelector((state) => state.ngodata);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const isAdmin = auth.profile?.usertype === 'admin';

  // The page and the table's search are kept while staff open a record and
  // come back (owner, 28 Oct); components/modern/pageState.js.
  const [page, setPage] = usePageState('ngos', 'page', Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);
  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  // Donation tallies from server-only /ngo_stats, keyed by ngoId.
  const [ngoStats, setNgoStats] = useState({});

  // NGO list is fetched on mount (no longer pre-fetched at login).
  useEffect(() => {
    dispatch(fetchNgos());
  }, [dispatch, fetchNgos]);

  useEffect(() => {
    api
      .fetchNgoStats()
      .then(setNgoStats)
      .catch(() => setNgoStats({}));
  }, []);

  const data = useMemo(() => (Array.isArray(ngodata.ngos) ? ngodata.ngos : []), [ngodata.ngos]);

  // ── Logo ──────────────────────────────────────────────────────────────
  const [rowData, setRowData] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const previewUrl = useMemo(() => (selectedImage ? URL.createObjectURL(selectedImage) : null), [selectedImage]);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const onImageClick = (row) => {
    if (isAdmin) {
      setRowData(row);
      setSelectedImage(null);
      setImageError(null);
    }
  };
  const handleImageModalClose = () => {
    if (imageLoading) return;
    setRowData(null);
    setSelectedImage(null);
    setImageError(null);
  };

  const handleSaveImage = async () => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    if (!selectedImage) {
      setImageError(t('choose_image_first'));
      return;
    }
    try {
      setImageLoading(true);
      setImageError(null);
      const url = await uploadNgoImage(rowData.id, selectedImage);
      await dispatch(editNgo({ ...rowData, image: url }, 'Update'));
      dispatch(fetchNgos());
      setRowData(null);
      setSelectedImage(null);
      showToast.success(t('ngos_image_saved', { defaultValue: 'Logo saved' }));
    } catch (error) {
      setImageError(error.message);
    } finally {
      setImageLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDelete = async (row) => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const ok = await confirmDialog({
      title: t('ngos_delete_title', { name: row.name || '', defaultValue: 'Delete the NGO “{{name}}”?' }),
      body: t('ngos_delete_body', { defaultValue: 'Customers can no longer donate miles to it. Its past donations stay in the reports.' }),
      confirmLabel: t('delete', { defaultValue: 'Delete' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    try {
      await dispatch(editNgo(row, 'Delete'));
    } catch (e) {
      showToast.error(e && e.message ? e.message : t('form_save_failed', { defaultValue: 'Could not save' }));
      return;
    }
    dispatch(fetchNgos());
    showToast.success(t('ngos_deleted', { defaultValue: 'NGO deleted' }));
  };

  const statOf = (row, key) => (ngoStats && ngoStats[row.id] ? ngoStats[row.id][key] : undefined);
  const symbolText = settings?.symbol ? ` (${settings.symbol})` : ' (ETB)';

  const columns = [
    {
      title: t('image', { defaultValue: 'Image' }),
      field: 'image',
      sortable: false,
      searchable: false,
      print: false,
      render: (row) => {
        const pic = row.image ? (
          <Box
            component="img"
            alt={isAdmin ? '' : row.name}
            src={row.image}
            sx={{ width: 48, height: 48, borderRadius: '8px', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box
            sx={{ width: 48, height: 48, borderRadius: '8px', bgcolor: c.SURFACE_ALT, color: c.TEXT_3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <ImageOutlinedIcon aria-hidden="true" />
          </Box>
        );
        return isAdmin ? (
          <ButtonBase
            onClick={() => onImageClick(row)}
            aria-label={`${t('update_image', { defaultValue: 'Update Image' })}: ${row.name || ''}`}
            sx={{ borderRadius: '8px', '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 } }}
          >
            {pic}
          </ButtonBase>
        ) : (
          pic
        );
      },
    },
    {
      title: t('ngo_name', { defaultValue: 'Name' }),
      field: 'name',
      render: (row) => (row.name ? <Box component="span" sx={{ fontWeight: 600 }}>{row.name}</Box> : null),
    },
    {
      title: t('ngo_description', { defaultValue: 'Description' }),
      field: 'description',
      minWidth: 200,
      render: (row) =>
        row.description ? (
          <Box component="span" title={row.description}>
            {row.description.length > 60 ? `${row.description.slice(0, 60)}…` : row.description}
          </Box>
        ) : null,
    },
    {
      title: t('ngo_active', { defaultValue: 'Active' }),
      field: 'active',
      type: 'status',
      sortValue: (row) => (row.active === true ? 1 : 0),
      searchValue: (row) => (row.active === true ? t('active', { defaultValue: 'Active' }) : t('inactive', { defaultValue: 'Inactive' })),
      exportValue: (row) => (row.active === true ? t('active', { defaultValue: 'Active' }) : t('inactive', { defaultValue: 'Inactive' })),
      status: (row) =>
        row.active === true
          ? { tone: 'success', icon: <CheckCircleOutlineRoundedIcon />, label: t('active', { defaultValue: 'Active' }) }
          : { tone: 'neutral', icon: <RemoveCircleOutlineRoundedIcon />, label: t('inactive', { defaultValue: 'Inactive' }) },
    },
    {
      id: 'miles',
      title: t('miles_donated', { defaultValue: 'Miles donated' }),
      type: 'number',
      searchable: false,
      sortValue: (row) => statOf(row, 'miles') || 0,
      exportValue: (row) => statOf(row, 'miles') ?? 0,
      render: (row) => formatNumber(statOf(row, 'miles') ?? 0),
    },
    {
      id: 'donationCount',
      title: t('donation_count', { defaultValue: 'Donations' }),
      type: 'number',
      searchable: false,
      sortValue: (row) => statOf(row, 'donationCount') || 0,
      exportValue: (row) => statOf(row, 'donationCount') ?? 0,
      render: (row) => formatNumber(statOf(row, 'donationCount') ?? 0),
    },
    {
      id: 'etbValue',
      title: `${t('donation_value', { defaultValue: 'Value' })}${symbolText}`,
      type: 'money',
      searchable: false,
      sortValue: (row) => statOf(row, 'etbValue') || 0,
      exportValue: (row) => statOf(row, 'etbValue') ?? 0,
      render: (row) => formatMoney(statOf(row, 'etbValue') ?? 0, settings),
    },
    {
      id: 'lastDonationAt',
      title: t('last_donation', { defaultValue: 'Last donation' }),
      type: 'datetime',
      searchable: false,
      sortValue: (row) => statOf(row, 'lastDonationAt') || 0,
      exportValue: (row) => (statOf(row, 'lastDonationAt') ? formatAddisDateTime(statOf(row, 'lastDonationAt')) : ''),
      render: (row) => (statOf(row, 'lastDonationAt') ? formatAddisDateTime(statOf(row, 'lastDonationAt')) : null),
    },
    { title: t('ngo_order', { defaultValue: 'Display order' }), field: 'order', type: 'number' },
  ];

  const navState = { state: { pageNo: page } };
  const goAdd = () => navigate('/ngos/editngo', navState);
  const goEdit = (row) => navigate(`/ngos/editngo/${row.id}`, navState);
  const addLabel = t('add_ngo', { defaultValue: 'Add NGO' });
  const title = t('ngo_management', { defaultValue: 'NGO Management' });

  const actions = [
    { key: 'edit', label: t('edit'), icon: <EditOutlinedIcon />, inline: true, onClick: goEdit },
    ...(isAdmin ? [{ key: 'image', label: t('update_image', { defaultValue: 'Update Image' }), icon: <AddPhotoAlternateOutlinedIcon />, onClick: onImageClick }] : []),
    { key: 'delete', label: t('ngos_delete', { defaultValue: 'Delete NGO' }), icon: <DeleteOutlineRoundedIcon />, destructive: true, onClick: handleDelete },
  ];

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader primaryAction={{ label: addLabel, icon: <AddRoundedIcon />, onClick: goAdd }} />
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey="id"
        loading={!!ngodata.loading && !data.length}
        refreshing={!!ngodata.loading && data.length > 0}
        countText={({ shown, total, query, loading }) =>
          loading
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
              : t('ngos_count', { count: total, defaultValue_one: '{{count}} NGO', defaultValue_other: '{{count}} NGOs', defaultValue: '{{count}} NGOs' })
        }
        rowLabel={(row) => row.name}
        actions={actions}
        searchPlaceholder={t('ngos_search', { defaultValue: 'Search name or description' })}
        resortKey={ngoStats}
        page={page}
        onPageChange={setPage}
        prefsKey="ngos"
        stateKey="ngos"
        minWidth={1100}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <VolunteerActivismOutlinedIcon />,
                title: t('ngos_empty', { defaultValue: 'No NGOs yet' }),
                body: t('ngos_empty_body', { defaultValue: 'Add an NGO so customers can donate their miles to it.' }),
                actionLabel: addLabel,
                onAction: goAdd,
              }
        }
      />

      <Dialog open={!!rowData} onClose={handleImageModalClose} fullWidth maxWidth="xs" fullScreen={phone} aria-labelledby="ngo-image-title">
        <DialogTitle id="ngo-image-title" sx={{ pr: 8 }}>
          {t('update_image', { defaultValue: 'Update Image' })}
          {rowData && rowData.name ? (
            <Typography component="span" sx={{ display: 'block', fontSize: 14, color: c.TEXT_3, fontWeight: 400 }}>
              {rowData.name}
            </Typography>
          ) : null}
          <IconButton
            aria-label={t('close_dialog', { defaultValue: 'Close' })}
            onClick={handleImageModalClose}
            disabled={imageLoading}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {imageError ? (
            <InlineMessage kind="error" sx={{ mb: 2 }}>
              {imageError}
            </InlineMessage>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 0.5 }}>
            {previewUrl || rowData?.image ? (
              <Box
                component="img"
                alt={t('ngo_image', { defaultValue: 'NGO image' })}
                src={previewUrl || rowData.image}
                sx={{ width: 200, height: 200, objectFit: 'cover', borderRadius: '12px', border: `1px solid ${c.OUTLINE}` }}
              />
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
              <Button variant="outlined" color="neutral" component="label" startIcon={<UploadFileOutlinedIcon />} disabled={imageLoading}>
                {selectedImage
                  ? t('car_types_image_other', { defaultValue: 'Choose another picture' })
                  : t('car_types_image_choose', { defaultValue: 'Choose a picture' })}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files && event.target.files[0];
                    if (file) {
                      setSelectedImage(file);
                      setImageError(null);
                    }
                    event.target.value = '';
                  }}
                />
              </Button>
              {selectedImage ? (
                <Button variant="text" color="neutral" onClick={() => setSelectedImage(null)} disabled={imageLoading}>
                  {t('car_types_image_remove', { defaultValue: 'Remove' })}
                </Button>
              ) : null}
            </Box>
            {selectedImage ? (
              <Typography sx={{ fontSize: 13.5, color: c.TEXT_3, overflowWrap: 'anywhere', textAlign: 'center' }}>{selectedImage.name}</Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={handleImageModalClose} disabled={imageLoading}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveImage}
            disabled={imageLoading}
            startIcon={imageLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {imageLoading ? t('form_saving', { defaultValue: 'Saving…' }) : t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
